/**
 * The user's saved dashboards and charts.
 *
 * Reads from the API rather than browser storage. Auto-save is meaningless
 * against localStorage: the dashboard would vanish on a cleared cache and
 * never appear on a second machine.
 *
 * The chart tray is what makes a dashboard composable after the fact. Charts
 * saved in separate conversations live here unattached, and selecting several
 * builds one dashboard from them.
 */
import {
  ArrowLeft,
  ChartColumn,
  Eye,
  EyeOff,
  FileText,
  LayoutDashboard,
  LayoutGrid,
  List,
  Plus,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArtifactView } from "../../components/ArtifactView";
import { DashboardView } from "../../components/DashboardView";
import { composedEventFor } from "../../services/artifactEvent";
import { SharePanel } from "../access/SharePanel";
import {
  ApiError,
  artifactsApi,
  chartsApi,
  dashboardsApi,
} from "../../services/api";
import type { ArtifactComposedEvent } from "../../services/agui";
import { devTrace } from "../../services/devTrace";
import type {
  ArtifactSummary,
  Dashboard,
  DashboardSummary,
  Panel,
  UserSession,
} from "../../types";
import { DashboardPreview } from "./DashboardPreview";
import { DocumentPreview } from "./DocumentPreview";
import { WorkspaceCard, WorkspaceCardSkeleton } from "./WorkspaceCard";
import {
  bucketGroups,
  chartLabel,
  type DashboardFilter,
  type DashboardGroup,
  type DashboardSort,
  FILTERS,
  groupVersions,
  matchesFilter,
  matchesSearch,
  outlineLabels,
  readableTitle,
  type SavedGroup,
  type SavedItem,
  SORTS,
  sortGroups,
} from "./workspaceView";

export type WorkspaceView = "dashboards" | "reports" | "artifacts";
type Layout = "grid" | "list";
type Tab = "items" | "charts";

// Reports and artifacts are both documents the agent writes, so they share one
// list under the name the menu entry uses.
const VIEWS: Record<
  WorkspaceView,
  { label: string; noun: string; plural: string; create: string; intro: string; published: string }
> = {
  dashboards: {
    label: "Dashboards",
    noun: "dashboard",
    plural: "Dashboards",
    create: "Create dashboard",
    intro: "Everything the AI Analyst builds is saved here. Combine saved charts into a dashboard at any time.",
    published: "Published",
  },
  reports: {
    label: "Reports",
    noun: "report",
    plural: "Reports",
    create: "Create report",
    intro: "Reports the AI Analyst writes are saved here automatically.",
    published: "Issued",
  },
  artifacts: {
    label: "Artifacts",
    noun: "artifact",
    plural: "Artifacts",
    create: "Create artifact",
    intro: "Documents the AI Analyst writes are saved here automatically.",
    published: "Issued",
  },
};

const PAGE_SIZE = 24;
const UNDO_MS = 8000;
const LAYOUT_KEY = "dmi.workspace.layout";

function storedLayout(): Layout {
  try {
    return window.localStorage.getItem(LAYOUT_KEY) === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
}

interface Undo {
  message: string;
  restore: () => Promise<void>;
}

interface WorkspacePageProps {
  session: UserSession;
  view?: WorkspaceView;
  onCreate?: (view: WorkspaceView) => void;
  /** Opens the blank canvas for this view. Without it, the header asks the analyst. */
  onStartCanvas?: (view: WorkspaceView) => void;
}

export function WorkspacePage({
  session,
  view = "dashboards",
  onCreate,
  onStartCanvas,
}: WorkspacePageProps) {
  const copy = VIEWS[view];
  const documents = view !== "dashboards";
  const [summaries, setSummaries] = useState<DashboardSummary[]>([]);
  const [artifacts, setArtifacts] = useState<ArtifactSummary[]>([]);
  const [charts, setCharts] = useState<Panel[]>([]);
  const [open, setOpen] = useState<Dashboard | null>(null);
  const [openDocument, setOpenDocument] =
    useState<ArtifactComposedEvent | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [combining, setCombining] = useState(false);
  const [tab, setTab] = useState<Tab>("items");
  const [filter, setFilter] = useState<DashboardFilter>("all");
  const [sort, setSort] = useState<DashboardSort>("recent");
  const [layout, setLayout] = useState<Layout>(storedLayout);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [undo, setUndo] = useState<Undo | null>(null);
  const undoTimer = useRef<number | undefined>(undefined);
  // Hidden is a view rather than a state the user has to manage, so the
  // workspace shows what is visible and offers the rest behind one filter.
  const showHidden = filter === "hidden";

  const load = useCallback(async () => {
    setError("");
    try {
      // All lists in parallel: none depends on another, and a workspace that
      // paints its dashboards a beat before its charts looks broken.
      const [dashboards, tray, written] = await Promise.all([
        dashboardsApi.list(showHidden),
        chartsApi.list(showHidden),
        artifactsApi.list(showHidden),
      ]);
      setSummaries(dashboards);
      setCharts(tray);
      setArtifacts(written);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "Your workspace could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }, [showHidden]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => () => window.clearTimeout(undoTimer.current), []);

  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [filter, query, sort, tab]);

  function chooseLayout(next: Layout) {
    setLayout(next);
    try {
      window.localStorage.setItem(LAYOUT_KEY, next);
    } catch {
      // A preference that cannot be remembered still applies to this visit.
    }
  }

  function offerUndo(message: string, restore: () => Promise<void>) {
    window.clearTimeout(undoTimer.current);
    setUndo({ message, restore });
    undoTimer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
  }

  async function runUndo() {
    if (!undo) return;
    const { restore } = undo;
    window.clearTimeout(undoTimer.current);
    setUndo(null);
    try {
      await restore();
    } catch {
      setError("That could not be undone. Find it under Hidden to restore it.");
    }
    await load();
  }

  const { groups, matching } = useMemo(() => {
    const found = summaries.filter(
      (item) => matchesFilter(item, filter) && matchesSearch(item, query),
    );
    return { groups: sortGroups(groupVersions(found), sort), matching: found.length };
  }, [summaries, filter, query, sort]);

  const { documentGroups, matchingDocuments } = useMemo(() => {
    const found = artifacts.filter(
      (item) => matchesFilter(item, filter) && matchesSearch(item, query),
    );
    return {
      documentGroups: sortGroups(groupVersions(found), sort),
      matchingDocuments: found.length,
    };
  }, [artifacts, filter, query, sort]);

  async function openDashboard(id: string) {
    setError("");
    try {
      setOpen(await dashboardsApi.get(id));
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "That dashboard could not be opened.",
      );
    }
  }

  /**
   * Opens a stored document in the same reader the conversation uses.
   *
   * The viewer takes the agent's event rather than a stored row, so the record
   * is shaped into one. `saved` is true by definition here: it was read back
   * out of the workspace.
   */
  async function openArtifact(id: string) {
    setError("");
    try {
      const [artifact, results] = await Promise.all([
        artifactsApi.get(id),
        // A document is still readable without its rows, so a failure here
        // must not stop it opening.
        artifactsApi.data(id).catch(() => []),
      ]);
      setOpenDocument(composedEventFor(artifact, results));
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "That document could not be opened.",
      );
    }
  }

  /** Every run of a document together, for the same reason as a dashboard's. */
  async function toggleDocumentGroupHidden({ latest, versions, title }: SavedGroup<ArtifactSummary>) {
    setError("");
    const hidden = !latest.hidden;
    const ids = versions.map((item) => item.id);
    const apply = (value: boolean) =>
      Promise.all(ids.map((id) => (value ? artifactsApi.hide(id) : artifactsApi.restore(id))));
    try {
      await apply(hidden);
      setArtifacts((current) => current.filter((item) => !ids.includes(item.id)));
      const what = versions.length > 1 ? `"${title}" (${versions.length} runs)` : `"${title}"`;
      offerUndo(`${hidden ? "Hid" : "Restored"} ${what}.`, async () => {
        await apply(!hidden);
      });
    } catch {
      setError(
        hidden
          ? "That document could not be hidden."
          : "That document could not be restored.",
      );
      await load();
    }
  }

  /**
   * Hides or restores every version of a request together: the card stands
   * for all of them, so hiding it must not leave older runs behind.
   */
  async function toggleGroupHidden({ latest, versions, title }: DashboardGroup) {
    setError("");
    const hidden = !latest.hidden;
    const ids = versions.map((item) => item.id);
    const apply = (value: boolean) =>
      Promise.all(ids.map((id) => dashboardsApi.setHidden(id, value)));
    try {
      await apply(hidden);
      // Dropped from whichever list is on screen, because the two views are
      // mutually exclusive: hiding removes it from the visible list, and
      // restoring removes it from the hidden one.
      setSummaries((current) => current.filter((item) => !ids.includes(item.id)));
      const what = versions.length > 1 ? `"${title}" (${versions.length} versions)` : `"${title}"`;
      offerUndo(`${hidden ? "Hid" : "Restored"} ${what}.`, async () => {
        await apply(!hidden);
      });
      devTrace.record({
        runId: latest.id,
        step: hidden ? "dashboard_hidden" : "dashboard_restored",
        actor: "Workspace",
        level: "info",
        summary: hidden
          ? `Hid ${what}. Nothing was deleted.`
          : `Restored ${what} to the workspace.`,
      });
    } catch {
      setError(
        hidden
          ? "That dashboard could not be hidden."
          : "That dashboard could not be restored.",
      );
      await load();
    }
  }

  async function renameDashboard(summary: DashboardSummary, name: string): Promise<boolean> {
    setError("");
    try {
      const updated = await dashboardsApi.update(summary.id, { name });
      setSummaries((current) =>
        current.map((item) =>
          item.id === summary.id
            ? { ...item, name: updated.name, updatedAt: updated.updatedAt ?? item.updatedAt }
            : item,
        ),
      );
      return true;
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "That dashboard could not be renamed.",
      );
      return false;
    }
  }

  function toggleChart(panelId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(panelId)) next.delete(panelId);
      else next.add(panelId);
      return next;
    });
  }

  async function combineSelected() {
    const panelIds = [...selected];
    if (panelIds.length === 0) return;

    setCombining(true);
    setError("");
    try {
      // Charts may come from different conversations, so each keeps the
      // contract it was authored against rather than inheriting one here.
      const created = await dashboardsApi.create({
        name: `Dashboard from ${panelIds.length} chart${
          panelIds.length === 1 ? "" : "s"
        }`,
        panelIds,
      });
      setSelected(new Set());
      await load();
      setOpen(created);
      devTrace.record({
        runId: created.id,
        step: "dashboard_composed_from_charts",
        actor: "Workspace",
        level: "success",
        summary: `Combined ${panelIds.length} saved charts into ${created.name}.`,
        output: { dashboardId: created.id, panels: panelIds.length },
      });
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "Those charts could not be combined.",
      );
    } finally {
      setCombining(false);
    }
  }

  async function toggleChartHidden(panel: Panel) {
    setError("");
    const hidden = !panel.hidden;
    try {
      await chartsApi.setHidden(panel.id, hidden);
      setCharts((current) => current.filter((item) => item.id !== panel.id));
      setSelected((current) => {
        const next = new Set(current);
        next.delete(panel.id);
        return next;
      });
      offerUndo(`${hidden ? "Hid" : "Restored"} "${panel.title}".`, async () => {
        await chartsApi.setHidden(panel.id, !hidden);
      });
    } catch {
      setError(
        hidden
          ? "That chart could not be hidden."
          : "That chart could not be restored.",
      );
    }
  }

  if (openDocument) {
    return (
      <div className="content-stack">
        <button
          type="button"
          className="button tertiary self-start"
          onClick={() => {
            setOpenDocument(null);
            void load();
          }}
        >
          <ArrowLeft size={16} /> Back to {copy.label.toLowerCase()}
        </button>
        <ArtifactView
          event={openDocument}
          onClose={() => {
            setOpenDocument(null);
            void load();
          }}
        />
        <SharePanel resourceType="artifact" resourceId={openDocument.artifactId} />
      </div>
    );
  }

  if (open) {
    return (
      <div className="content-stack">
        <button
          type="button"
          className="button tertiary self-start"
          onClick={() => {
            setOpen(null);
            void load();
          }}
        >
          <ArrowLeft size={16} /> Back to {copy.label.toLowerCase()}
        </button>
        <DashboardView dashboard={open} onRenamed={setOpen} />
        <SharePanel
          resourceType="dashboard"
          resourceId={open.id}
          contextId={open.contextId}
        />
      </div>
    );
  }

  const itemCount = documents ? artifacts.length : summaries.length;
  const shownCount = documents ? documentGroups.length : groups.length;
  const earlier = documents
    ? matchingDocuments - documentGroups.length
    : matching - groups.length;
  const runNoun = documents ? "run" : "version";

  function chooseTab(next: Tab) {
    setTab(next);
    // Charts have no status or owner to filter by; only Hidden carries over.
    if (next === "charts" && filter !== "hidden") setFilter("all");
  }
  const filters = FILTERS.map((item) =>
    item.id === "published" ? { ...item, label: copy.published } : item,
  );
  const grouped = sort === "recent" && !query.trim();

  function emptyState() {
    const Icon = documents ? FileText : LayoutDashboard;
    const noun = `${copy.noun}s`;
    let title = `No ${noun} yet`;
    let body = `Ask the AI Analyst a question and the ${copy.noun} it builds is saved here.`;
    let action = (
      <button type="button" className="button primary" onClick={() => onCreate?.(view)}>
        <Sparkles size={15} /> Ask the AI Analyst
      </button>
    );
    if (query.trim()) {
      title = `No ${noun} match "${query.trim()}"`;
      body = "Try a different word, or clear the search.";
      action = (
        <button type="button" className="button secondary" onClick={() => setQuery("")}>
          Clear search
        </button>
      );
    } else if (showHidden) {
      title = "Nothing is hidden";
      body = `${copy.plural} you hide are kept here and can be restored.`;
      action = (
        <button type="button" className="button secondary" onClick={() => setFilter("all")}>
          Show all {noun}
        </button>
      );
    } else if (filter !== "all") {
      title = `No ${filters.find((item) => item.id === filter)?.label.toLowerCase()} ${noun}`;
      body = "Other filters may have what you are looking for.";
      action = (
        <button type="button" className="button secondary" onClick={() => setFilter("all")}>
          Show all {noun}
        </button>
      );
    }
    return (
      <div className="empty-state">
        <Icon size={31} />
        <strong>{title}</strong>
        <p>{body}</p>
        {action}
      </div>
    );
  }

  function skeletons() {
    return (
      <div className={`workspace-cards ${layout}`} role="status" aria-label={`Loading ${copy.noun}s`}>
        {Array.from({ length: 6 }, (_, index) => (
          <WorkspaceCardSkeleton key={index} layout={layout} />
        ))}
      </div>
    );
  }

  /** The first page of groups, under time headings when sorted by recency. */
  function sectionsOf<T extends SavedItem>(
    all: SavedGroup<T>[],
    card: (group: SavedGroup<T>) => ReactNode,
  ) {
    const visible = all.slice(0, limit);
    const sections = grouped
      ? bucketGroups(visible)
      : [{ bucket: null, groups: visible }];
    return sections.map((section) => (
      <section
        key={section.bucket ?? "all"}
        className="workspace-section"
        aria-label={section.bucket ?? undefined}
      >
        {section.bucket && <h3 className="workspace-section-title">{section.bucket}</h3>}
        <div className={`workspace-cards ${layout}`}>{section.groups.map(card)}</div>
      </section>
    ));
  }

  function dashboardCards() {
    return sectionsOf(groups, (group) => {
            const summary = group.latest;
            return (
              <WorkspaceCard
                key={group.key}
                layout={layout}
                title={group.title}
                icon={<LayoutDashboard size={17} />}
                details={
                  <>
                    {summary.panelCount} panel{summary.panelCount === 1 ? "" : "s"}
                    {summary.chartTypes.length > 0
                      && ` · ${summary.chartTypes.map(chartLabel).join(", ")}`}
                    {summary.shared && " · Shared with you"}
                  </>
                }
                status={{
                  className: summary.status,
                  label: summary.status === "draft" ? "Draft" : copy.published,
                }}
                updatedAt={summary.updatedAt}
                preview={
                  <DashboardPreview
                    chartTypes={summary.chartTypes}
                    panelCount={summary.panelCount}
                  />
                }
                onOpen={() => void openDashboard(summary.id)}
                versions={group.versions.slice(1).map((version) => ({
                  id: version.id,
                  label: `${version.status === "draft" ? "Draft" : copy.published} · ${
                    version.panelCount
                  } panel${version.panelCount === 1 ? "" : "s"}`,
                  updatedAt: version.updatedAt,
                }))}
                onOpenVersion={(id) => void openDashboard(id)}
                onRename={
                  summary.shared ? undefined : (name) => renameDashboard(summary, name)
                }
                actions={[
                  {
                    label: summary.hidden
                      ? "Restore to workspace"
                      : group.versions.length > 1
                        ? `Hide all ${group.versions.length} versions`
                        : "Hide from workspace",
                    icon: summary.hidden ? <Eye size={14} /> : <EyeOff size={14} />,
                    onSelect: () => void toggleGroupHidden(group),
                  },
                ]}
              />
            );
    });
  }

  function documentCards() {
    return sectionsOf(documentGroups, (group) => {
      const summary = group.latest;
      const holds = outlineLabels(summary.outline);
      return (
        <WorkspaceCard
          key={group.key}
          layout={layout}
          title={group.title}
          icon={<FileText size={17} />}
          details={
            <>
              {summary.sectionCount} section{summary.sectionCount === 1 ? "" : "s"}
              {holds.length > 0 && ` · ${holds.join(", ")}`}
              {summary.currentVersion > 1 && ` · Revision ${summary.currentVersion}`}
              {summary.shared && " · Shared with you"}
            </>
          }
          status={{
            className: summary.status,
            label: summary.status === "draft" ? "Draft" : copy.published,
          }}
          updatedAt={summary.updatedAt}
          preview={<DocumentPreview outline={summary.outline} />}
          footer={
            summary.subject ? (
              <p className="workspace-card-subject" title={summary.subject}>
                {summary.subject}
              </p>
            ) : null
          }
          onOpen={() => void openArtifact(summary.id)}
          versions={group.versions.slice(1).map((version) => ({
            id: version.id,
            label: `${version.status === "draft" ? "Draft" : copy.published} · ${
              version.sectionCount
            } section${version.sectionCount === 1 ? "" : "s"}`,
            updatedAt: version.updatedAt,
          }))}
          versionNoun="runs"
          onOpenVersion={(id) => void openArtifact(id)}
          // Hide, never delete: a document that has been sent cannot be made
          // to have never existed.
          actions={[
            {
              label: summary.hidden
                ? "Restore to workspace"
                : group.versions.length > 1
                  ? `Hide all ${group.versions.length} runs`
                  : "Hide from workspace",
              icon: summary.hidden ? <Eye size={14} /> : <EyeOff size={14} />,
              onSelect: () => void toggleDocumentGroupHidden(group),
            },
          ]}
        />
      );
    });
  }

  return (
    <div className="content-stack workspace-page">
      <header className="workspace-header">
        <div>
          <h1>{session.displayName}&apos;s workspace</h1>
          <p>{copy.intro}</p>
        </div>
        <button
          type="button"
          className="button primary"
          onClick={() => (onStartCanvas ? onStartCanvas(view) : onCreate?.(view))}
        >
          <Plus size={16} /> {copy.create}
        </button>
      </header>

      {error && (
        <p className="inline-notice warning" role="alert">
          {error}
        </p>
      )}

      <div className="workspace-tabs" role="tablist" aria-label="Workspace">
        <button
          type="button"
          role="tab"
          className="workspace-tab"
          aria-selected={tab === "items"}
          onClick={() => chooseTab("items")}
        >
          {documents ? <FileText size={16} /> : <LayoutDashboard size={16} />}
          {copy.label}
          <span className="workspace-count">{itemCount}</span>
        </button>
        {!documents && (
          <button
            type="button"
            role="tab"
            className="workspace-tab"
            aria-selected={tab === "charts"}
            onClick={() => chooseTab("charts")}
          >
            <ChartColumn size={16} /> Saved charts
            <span className="workspace-count">{charts.length}</span>
          </button>
        )}
      </div>

      <section className="panel workspace-panel">
        <div className="workspace-toolbar">
          <div className="workspace-filter-tabs" role="group" aria-label="Filter">
            {filters
              .filter((item) => tab === "items" || item.id === "all" || item.id === "hidden")
              .map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={filter === item.id ? "active" : ""}
                  aria-pressed={filter === item.id}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                </button>
              ))}
          </div>
          {tab === "items" && (
            <div className="workspace-controls">
              <label className="search-field">
                <Search size={16} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={`Search ${copy.noun}s`}
                  aria-label={`Search ${copy.noun}s`}
                />
                {query && (
                  <button type="button" onClick={() => setQuery("")} aria-label="Clear search">
                    <X size={14} />
                  </button>
                )}
              </label>
              <label className="filter-field workspace-sort">
                <span className="sr-only">Sort by</span>
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value as DashboardSort)}
                  aria-label="Sort by"
                >
                  {SORTS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.id === "panels" && documents ? "Most sections" : item.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="workspace-layout" role="group" aria-label="Layout">
                <button
                  type="button"
                  className="icon-button"
                  aria-pressed={layout === "grid"}
                  aria-label="Grid view"
                  title="Grid view"
                  onClick={() => chooseLayout("grid")}
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-pressed={layout === "list"}
                  aria-label="List view"
                  title="List view"
                  onClick={() => chooseLayout("list")}
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {tab === "items" && (
          <>
            {!loading && shownCount > 0 && (
              <p className="workspace-summary">
                {`${shownCount}${showHidden ? " hidden" : ""} ${copy.noun}${
                  shownCount === 1 ? "" : "s"
                }`}
                {earlier > 0 && ` · ${earlier} earlier ${runNoun}${earlier === 1 ? "" : "s"} grouped`}
              </p>
            )}
            {loading
              ? skeletons()
              : shownCount === 0
                ? emptyState()
                : documents
                  ? documentCards()
                  : dashboardCards()}
            {!loading && shownCount > limit && (
              <div className="workspace-more">
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => setLimit((value) => value + PAGE_SIZE)}
                >
                  Show more ({shownCount - limit} remaining)
                </button>
              </div>
            )}
          </>
        )}

        {tab === "charts" && (
          <>
            <p className="workspace-summary">
              {showHidden
                ? `${charts.length} hidden chart${charts.length === 1 ? "" : "s"}`
                : `Charts you saved on their own, not yet on a dashboard. Select several to combine them.`}
            </p>
            {charts.length > 0 ? (
              <ul className="chart-tray">
                {charts.map((panel) => (
                  <li key={panel.id} className={selected.has(panel.id) ? "selected" : ""}>
                    <label className="chart-tray-item">
                      <input
                        type="checkbox"
                        checked={selected.has(panel.id)}
                        onChange={() => toggleChart(panel.id)}
                      />
                      <span className="dashboard-icon">
                        <ChartColumn size={17} />
                      </span>
                      <span className="chart-tray-label">
                        <strong>{readableTitle(panel.title)}</strong>
                        <small>
                          {chartLabel(panel.chartType)}
                          {panel.contextId ? ` · ${panel.contextId}` : ""}
                        </small>
                      </span>
                    </label>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => void toggleChartHidden(panel)}
                      title={panel.hidden ? "Restore to tray" : "Hide from tray"}
                      aria-label={
                        panel.hidden
                          ? `Restore ${panel.title}`
                          : `Hide ${panel.title}`
                      }
                    >
                      {panel.hidden ? <Eye size={15} /> : <EyeOff size={15} />}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              !loading && (
                <div className="empty-state small">
                  <ChartColumn size={23} />
                  <strong>
                    {showHidden ? "No hidden charts" : "No saved charts"}
                  </strong>
                  <p>
                    {showHidden
                      ? "Charts you hide are kept here and can be restored."
                      : "Charts you save from a conversation appear here."}
                  </p>
                </div>
              )
            )}
          </>
        )}
      </section>

      {/* The requirement in one control: pick charts made separately and turn
          them into a single dashboard. Pinned while a selection exists, so it
          is never scrolled out of reach. */}
      {tab === "charts" && selected.size > 0 && (
        <div className="workspace-combine-bar" role="region" aria-label="Selected charts">
          <span>
            {selected.size} chart{selected.size === 1 ? "" : "s"} selected
          </span>
          <button type="button" className="button tertiary" onClick={() => setSelected(new Set())}>
            Clear
          </button>
          <button
            type="button"
            className="button primary"
            onClick={() => void combineSelected()}
            disabled={combining}
          >
            <Plus size={16} />
            {combining ? "Combining…" : "Combine into a dashboard"}
          </button>
        </div>
      )}

      {undo && (
        <div className="workspace-toast" role="status">
          <span>{undo.message}</span>
          <button type="button" className="workspace-toast-undo" onClick={() => void runUndo()}>
            Undo
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label="Dismiss"
            onClick={() => setUndo(null)}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
